import { useEffect, useMemo, useState } from 'react'
import { Box, Button, LinearProgress, Paper, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { analyzeDocument } from './dummyAi'

type DocId = 'hop-dong' | 'img' | 'email'

const DOCS: { id: DocId; name: string; size: string; badge: 'PDF' | 'IMG' | 'EML' }[] = [
  { id: 'hop-dong', name: 'HĐMB_căn_hộ_ABC_2023.pdf', size: '2.3 MB', badge: 'PDF' },
  { id: 'img', name: 'Ảnh_bàn_giao_căn_hộ_2023-02-18.jpg', size: '890 KB', badge: 'IMG' },
  { id: 'email', name: 'Email_khiếu_nại_hiệu_lực_2023-03.eml', size: '45 KB', badge: 'EML' },
]

const PROGRESS_TEXTS = ['Đang quét tài liệu...', 'Phát hiện điểm mâu thuẫn...', 'Đang tạo Timeline...', 'Hoàn tất phân tích']

export function DocumentAnalysisTab() {
  const theme = useTheme()
  const [analyzing, setAnalyzing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [progressText, setProgressText] = useState(PROGRESS_TEXTS[0])
  const [selectedDoc, setSelectedDoc] = useState<DocId | null>(null)
  const [result, setResult] = useState<Awaited<ReturnType<typeof analyzeDocument>> | null>(null)

  const defaultResult = useMemo<Awaited<ReturnType<typeof analyzeDocument>>>(
    () => ({
      timeline: [
        { date: '02/2023', event: 'Ký HĐMB căn hộ và đặt cọc', type: 'normal', source: 'HĐMB_căn_hộ_ABC_2023.pdf' },
        { date: '03/2023', event: 'Phát sinh tranh luận về mốc hiệu lực', type: 'warning', source: 'Email_khiếu_nại_hiệu_lực_2023-03.eml' },
        { date: '06/2023', event: 'Khiếu nại chính thức về bàn giao/vi phạm', type: 'conflict', source: 'Email_khiếu_nại_hiệu_lực_2023-03.eml' },
        { date: '10/2023', event: 'Nộp đơn khởi kiện yêu cầu giải quyết tranh chấp', type: 'normal', source: 'Đơn khởi kiện (bản scan)' },
      ],
      conflicts: [
        {
          id: 1,
          severity: 'high',
          desc: 'Điều 7 ↔ Điều 12: Ngày hiệu lực không thống nhất (01/03/2023 vs 15/02/2023)',
          locationA: { page: 3, line: 12 },
          locationB: { page: 7, line: 4 },
        },
        {
          id: 2,
          severity: 'medium',
          desc: 'HĐ chính ↔ Phụ lục: Tiến độ/giá trị thanh toán thể hiện không khớp',
          locationA: { page: 10, line: 2 },
          locationB: { page: 14, line: 6 },
        },
      ],
      summary: 'Có mâu thuẫn mốc hiệu lực và dấu hiệu không khớp tiến độ/giá trị giữa HĐ và phụ lục — cần đối chiếu email, biên bản bàn giao và chứng từ thanh toán.',
      riskScore: 78,
    }),
    [],
  )

  const handleAnalyze = async () => {
    setAnalyzing(true)
    setProgress(0)
    try {
      setSelectedDoc((prev) => prev ?? 'hop-dong')
      // Yêu cầu: bấm "Phân tích" là hiển thị kết quả ngay (mock).
      setResult(defaultResult)
      let ti = 0
      setProgressText(PROGRESS_TEXTS[0])
      const iv = window.setInterval(() => {
        setProgress((p) => {
          const next = Math.min(100, p + Math.random() * 25)
          return next
        })
        setProgressText(PROGRESS_TEXTS[Math.min(PROGRESS_TEXTS.length - 1, ti)])
        ti += 1
      }, 600)

      const r = await analyzeDocument([])
      window.clearInterval(iv)
      setProgress(100)
      setProgressText(PROGRESS_TEXTS[PROGRESS_TEXTS.length - 1])
      void r
      setResult(defaultResult)
    } finally {
      setAnalyzing(false)
    }
  }

  useEffect(() => {
    // Yêu cầu: chuyển vào tab là hiển thị kết quả luôn (mock dữ liệu use case thực tế).
    setResult(defaultResult)
    setProgress(100)
    setProgressText(PROGRESS_TEXTS[PROGRESS_TEXTS.length - 1])
  }, [defaultResult])

  const timelineColor = (t: string) =>
    t === 'conflict'
      ? theme.palette.error.main
      : t === 'warning'
        ? theme.palette.warning.main
        : theme.palette.info.main

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          minHeight: 0,
          gap: 1.5,
        }}
      >
        {/* Left: files */}
        <Paper
          elevation={0}
          sx={{
            width: { xs: '100%', md: 240 },
            flexShrink: 0,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
            bgcolor: 'background.paper',
            boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
          }}
        >
          <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 700, color: 'text.secondary' }}>
            TÀI LIỆU ĐÃ NỘP
          </Typography>
          <Stack spacing={1}>
            {DOCS.map((d) => (
              <Paper
                key={d.id}
                variant="outlined"
                onClick={() => setSelectedDoc(d.id)}
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  borderColor: selectedDoc === d.id ? 'primary.main' : 'divider',
                  bgcolor: 'background.paper',
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      bgcolor:
                        d.badge === 'PDF'
                          ? alpha(theme.palette.error.main, 0.12)
                          : d.badge === 'IMG'
                            ? alpha(theme.palette.info.main, 0.12)
                            : alpha(theme.palette.success.main, 0.16),
                      color:
                        d.badge === 'PDF'
                          ? theme.palette.error.dark
                          : d.badge === 'IMG'
                            ? theme.palette.info.dark
                            : theme.palette.success.dark,
                      flexShrink: 0,
                    }}
                  >
                    {d.badge}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      title={d.name}
                      sx={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        lineHeight: 1.35,
                        display: 'block',
                      }}
                    >
                      {d.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {d.size}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
          <Box sx={{ borderTop: 1, borderColor: 'divider', my: 1.5 }} />
          <Button variant="contained" fullWidth onClick={handleAnalyze} disabled={analyzing} sx={{ fontSize: 12 }}>
            {result ? 'Phân tích lại' : 'Phân tích với AI'}
          </Button>
          {analyzing && (
            <Box sx={{ mt: 1.25 }}>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 4, borderRadius: 1 }} />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {progressText}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Middle: timeline + viewer + findings */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {result && (
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}` }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 700, color: 'text.secondary' }}>
                TIMELINE SỰ KIỆN
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                Mục tiêu: sắp xếp các mốc thời gian để đối chiếu chứng cứ, nghĩa vụ và trách nhiệm theo từng giai đoạn vụ việc.
              </Typography>
              <Stack spacing={1.25}>
                {result.timeline.map((ev, idx) => (
                  <Stack key={`${ev.date}-${ev.event}`} direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: timelineColor(ev.type), mt: 0.25 }} />
                      {idx < result.timeline.length - 1 && <Box sx={{ width: 1, flex: 1, minHeight: 12, bgcolor: 'divider', mt: 0.5 }} />}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" fontWeight={700} sx={{ color: timelineColor(ev.type), display: 'block' }}>
                        {ev.date}
                      </Typography>
                      <Typography variant="body2">{ev.event}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        Nguồn: {ev.source}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}

          {result && (
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}` }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 700, color: 'text.secondary' }}>
                ĐIỂM MÂU THUẪN
              </Typography>
              <Box sx={{ p: 1, borderLeft: `3px solid ${theme.palette.error.main}`, bgcolor: alpha(theme.palette.error.main, 0.08), mb: 1, borderRadius: 1.5 }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.error.dark, display: 'block' }}>
                  Điều 7 ↔ Điều 12 (Hiệu lực)
                </Typography>
                <Typography variant="body2">Ngày hiệu lực không thống nhất: 01/03/2023 vs 15/02/2023</Typography>
              </Box>
              <Box sx={{ p: 1, borderLeft: `3px solid ${theme.palette.warning.main}`, bgcolor: alpha(theme.palette.warning.main, 0.12), borderRadius: 1.5 }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.warning.dark, display: 'block' }}>
                  HĐ chính ↔ Phụ lục (Giá trị/tiến độ)
                </Typography>
                <Typography variant="body2">Số tiền/tiến độ thanh toán thể hiện không khớp giữa các tài liệu</Typography>
              </Box>
            </Paper>
          )}

          {result && (
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}` }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 700, color: 'text.secondary' }}>
                KẾT LUẬN PHÂN TÍCH (AI)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                Qua đối chiếu HĐMB, email khiếu nại và tài liệu bàn giao, hệ thống phát hiện mâu thuẫn mốc hiệu lực và dấu hiệu không thống nhất về tiến độ/giá trị thanh toán.
                Khuyến nghị: chuẩn hóa 01 mốc hiệu lực được các bên xác nhận; lập bảng dòng tiền (sao kê + ủy nhiệm chi) theo timeline để xác định trách nhiệm và yêu cầu.
              </Typography>
            </Paper>
          )}
        </Box>

        {/* Right column removed per latest requirements */}
      </Box>
    </Box>
  )
}
